import React, { FC, useEffect, useState } from 'react';
import Lottie from 'react-lottie';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useUserInfo } from '@repository/auth/useUserInfo';
import PHSPACCModal from '@components/phs/test/paccGate/modals';
import {
  useApplyPCPTermsCheckNew,
  useApplyPPPTermsCheckNew,
  useMutationTutorialTermsCheckNew,
} from '@repository/userTermsAgrees/useUserTermsAgrees';
import { getAccessToken } from '@repository/AccessToken';
import LoginModal from '@components/loginModal';
import asteriskWhite from '@graphic/asteriskWhite.json';
import { JOBDA_SUPPORT_EMAIL } from '@domain/constant/contact';
import { PhsModalType } from '@domain/constant/phsModalType';
import { RouteUrl } from '@utils/routeUtil';
import useKcbIdentity from '@utils/hooks/useKcbIdentity';

import classnames from 'classnames/bind';
import styles from './pshPaccGate.module.scss';
import { usePACCApplyStatus, usePACCMaxApplyCount } from '@repository/phs/pacc/apply/usePhsPaccApply';
const cx = classnames.bind(styles);

const options = {
  loop: true,
  autoplay: true,
  animationData: asteriskWhite,
};

const PaccTest: FC = () => {
  const [modalState, setModalState] = useState<PhsModalType>(null);
  const [loginModalVisible, setLoginModalVisible] = useState(false);

  const { query, isReady, replace } = useRouter();
  const { data: userInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;
  const { data: paccMaxApplyCount } = usePACCMaxApplyCount();
  const { data: applyStatusData } = usePACCApplyStatus(isLogin);
  const { mutateAsync: checkTutorialTermsMutateAsync } = useMutationTutorialTermsCheckNew();
  const { mutateAsync: checkApplyPCPTermsMutateAsync } = useApplyPCPTermsCheckNew();
  const { mutateAsync: checkApplyPPPTermsMutateAsync } = useApplyPPPTermsCheckNew();
  const { isVerification } = useKcbIdentity({
    onError: () => setModalState(null),
    onSuccess: () => setModalState('TEST_TERMS'),
  });

  const openLoginModal = () => setLoginModalVisible(true);
  const closeLoginModal = () => setLoginModalVisible(false);
  const chanceCount = applyStatusData?.chanceCount || 0;
  const isProgress = applyStatusData?.isProgress || false;

  useEffect(() => {
    if (isReady) {
      if (getAccessToken() && query.open === 'tutorial') {
        onClickTutorialApplyBtn();
        replace(RouteUrl.PHS_PACC_TEST_GATE, RouteUrl.PHS_PACC_TEST_GATE, { shallow: true });
      }
    }
  }, [isReady]);

  // 튜토리얼로 연습하기
  const onClickTutorialApplyBtn = async () => {
    if (!isLogin) {
      openLoginModal();
      return;
    }

    //튜토리얼 약관 동의 했는지 체크
    const rs = await checkTutorialTermsMutateAsync();
    if (rs.existsNewTermsAgree) {
      setModalState('TUTORIAL_TERMS');
      return;
    }

    setModalState('TUTORIAL_GUIDE');
  };

  // 응시하기
  const onClickApplyBtn = async () => {
    if (!isLogin) {
      openLoginModal();
      return;
    }

    //본인 인증 체크
    if (!isVerification) {
      setModalState('VERIFICATION');
      return;
    }

    if (chanceCount < 1) {
      setModalState('NO_MORE_CHANCE');
      return;
    }

    //약관 동의 여부 체크
    const applyPCPRs = await checkApplyPCPTermsMutateAsync();
    const applyPPPRs = await checkApplyPPPTermsMutateAsync();
    if (applyPCPRs.existsNewTermsAgree || applyPPPRs.existsNewTermsAgree) {
      setModalState('TEST_TERMS');
      return;
    }

    setModalState('TEST_GUIDE');
  };

  //phs_paccaGate_btn_pooltrg : GA 분석용  클래스
  const applyButtonEl = isProgress ? (
    <a className={cx('btn', 'phs_paccaGate_btn_pooltrg')} role={'button'} onClick={() => setModalState('APPLY')}>
      <div className={cx('textWrap')}>
        <span className={cx('badge')}>{chanceCount}회 남음</span>이어하기
      </div>
    </a>
  ) : (
    <a className={cx('btn', 'phs_paccGate_btn_pooltrg')} role={'button'} onClick={onClickApplyBtn}>
      <div className={cx('textWrap')}>
        {isLogin && <span className={cx('badge')}>{chanceCount}회 남음</span>}
        실전 응시하기
      </div>
    </a>
  );

  return (
    <div className={cx('accaGateArea')}>
      <div className={cx('visualArea')}>
        <div className={cx('visualWrap')}>
          <Lottie options={options} />
        </div>
      </div>

      <div className={cx('infoArea')}>
        <div className={cx('infoInner')}>
          <strong className={cx('title')}>
            개발자 역검은 개발자에게 필요한 역량을
            <br />
            측정하는 진단 도구입니다.
          </strong>
          <em className={cx('subTitle')}>개발자 역검은 성향파악, 전략게임, 영상면접으로 이루어져 있습니다.</em>
          <p className={cx('desc')}>
            검사는 약 50분 정도 소요되며, 매월 {paccMaxApplyCount}회의 응시기회가 제공됩니다.
          </p>

          <div className={cx('btnArea')}>
            <a className={cx('btn')} role={'button'} onClick={onClickTutorialApplyBtn}>
              <div className={cx('textWrap')}>튜토리얼로 연습하기</div>
            </a>
            {applyButtonEl}
            <Link href={RouteUrl.PROFILE_PHS_PACCA} legacyBehavior>
              <a className={cx('btn')} target="_blank" rel="noreferrer" role="button">
                <div className={cx('textWrap')}>결과표 보러가기</div>
              </a>
            </Link>
          </div>
          <div className={cx('notice')}>
            응시에 문제가 생길 경우 <a href={`mailto:${JOBDA_SUPPORT_EMAIL}`}>{JOBDA_SUPPORT_EMAIL}</a>로 문의해주세요.
          </div>
        </div>
      </div>

      <PHSPACCModal modalState={modalState} setModalState={setModalState} />
      <LoginModal
        subTitle="채용정보 · 역량검사 · 매칭까지"
        title="취업기회 지금 바로 잡다!"
        visible={loginModalVisible}
        onClose={closeLoginModal}
      />
    </div>
  );
};

export default PaccTest;
